<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
       <!-- Dynamic Parent -->
      <router-link v-if="fromNotifications" :to="{ name: 'ViewPendingNotifications', query: { entityType: route.query.entityType, statusInd: route.query.statusInd } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        Notifications
      </router-link>

      <router-link v-else :to="{ name: 'ViewPVMemos' }" class="mb-0 main-identifier fw-semibold text-decoration-none" title="Go back to PV Memos">
        PV Memos
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View PV Memo</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer">
        <form @submit.prevent="step === totalSteps ? handleSubmit() : handleNext()">
          <div class="crud-form position-relative">

            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">PV Memo</h1>
              </div>

              <div class="memo-content-area position-relative">
                <div v-if="loadingMemo" class="loading-overlay text-center">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0 text-muted">Loading memo details...</p>
                </div>

                <div class="form-body mt-2 mb-3" v-if="isDataLoaded">
                  <div v-if="step === 1">

                    <div class="form-row">
                      <div class="form-row-label">
                        <label for="fileName" class="form-label">File Title:</label>
                      </div>
                      <div class="form-row-input">
                        <input id="fileName" type="text" class="form-control form-view-only-input" v-model="form.fileTitle"
                          placeholder="" readonly />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-label">
                        <label for="fileNumber" class="form-label">File Number:</label>
                      </div>
                      <div class="form-row-input">
                        <input id="fileNumber" type="text" class="form-control form-view-only-input"
                          v-model="form.fileNumber" placeholder="" readonly />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-label">
                        <label for="createdDate" class="form-label">Created Date:</label>
                      </div>
                      <div class="form-row-input">
                        <input id="createdDate" type="text" class="form-control form-view-only-input"
                          :value="formatDate(form.createdDate)" placeholder="" readonly />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-label">
                        <label for="OriginatingMda" class="form-label">Originating M.D.A.:</label>
                      </div>
                      <div class="form-row-input">
                        <input id="OriginatingMda" type="text" class="form-control form-view-only-input"
                          v-model="form.originatingMda" placeholder="" readonly />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-label">
                        <label for="amountApproved" class="form-label">Amount Approved:</label>
                      </div>
                      <div class="form-row-input">
                        <input id="amountApproved" type="text" class="form-control form-view-only-input"
                          :value="'₦' + Number(form.amountApproved || 0).toLocaleString()" placeholder="" readonly />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-label">
                        <label class="form-label">Status:</label>
                      </div>
                      <div class="form-row-input">
                        <input type="text" class="form-control form-view-only-input" :value="status" readonly>
                      </div>
                    </div>

                    <div class="form-row" v-if="form.grpHeadGrpMemRemarks && (isReturned || isRejected)">
                      <div class="form-row-label">
                        <label class="form-label">Remarks:</label>
                      </div>
                      <div class="form-row-input">
                        <input type="text" class="form-control form-view-only-input" v-model="form.grpHeadGrpMemRemarks"
                          placeholder="" readonly>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>


            <!-- Main Actions Container -->
            <div v-if="rolesLoaded && isDataLoaded" class="action-buttons-container">
              <div v-if="step === totalSteps" class="action-buttons-wrapper">

                <!-- Edit Memo - Always allowed if editable (even if returned) -->
                <!-- <div v-if="rolesLoaded && hasAccess(['registryOfficer', ''])" class="action-button">
                  <button type="button" class="btn btn-primary submit-btn" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    <router-link :to="{ name: 'UpdateMemo', params: { id: form.memoId } }" class="text-decoration-none"
                      style="color: #fff">
                      {{ (loading ? 'Editing...' : 'Edit Memo') }}
                    </router-link>
                  </button>
                </div> -->

                <!-- PV Initiator -->
                <template v-if="rolesLoaded && hasAccess(['pvInitiator'])">
                  <div v-if="pvExistsForMemo" class="alert alert-warning mb-0" role="alert">
                    A payment voucher already exists for this memo.
                  </div>

                  <!-- Generate PV from this memo -->
                  <div v-if="form.memoId && !appraisalInitiated && !pvExistsForMemo" class="action-button">
                    <button type="button" class="btn btn-info generate-pv-btn" :disabled="loading">
                      <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      <router-link
                        :to="{ name: 'ViewGeneratePVWithoutWarrant', params: { memoId: form.memoId }, query: { memoId: form.memoId } }"
                        class="text-decoration-none" style="color: #fff">
                        {{ (loading ? 'Loading...' : 'Generate PV') }}
                      </router-link>
                    </button>
                  </div>
                </template>

                <!-- Group Head, QSSU, Super Admin -->
                <template v-if="rolesLoaded && hasAccess(['qssu', 'registryOfficer', '']) && !isReturned">
                  <!-- Create Memo from PV Memo - Group Head/Group Member only -->
                  <div v-if="rolesLoaded && hasAccess(['qssu', 'registryOfficer']) && !memoExistsForPvMemo"
                    class="action-button">
                    <button type="button" class="btn btn-primary submit-btn" @click="handleCreateMemoFromPV"
                      :disabled="loadingCreateMemo || loadingReturn || submitted" style="margin: 0%;">
                      <span v-if="loadingCreateMemo" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ (loadingCreateMemo ? 'Creating...' : 'Create Memo') }}
                    </button>
                  </div>

                  <!-- Initiate Appraisal - Only show if memoId exists -->
                  <!-- <div v-if="form.memoId" class="action-button">
                    <button type="button" class="btn btn-success accept-btn" :disabled="loading">
                      <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      <router-link :to="{ name: 'GenerateAppraisal', params: { id: form.memoId } }"
                        class="text-decoration-none" style="color: #fff">
                        {{ (loading ? 'Initiating...' : 'Initiate Appraisal') }}
                      </router-link>
                    </button>
                  </div> -->

                  <!-- Return Memo -->
                  <!-- <div class="action-button">
                    <button type="button" class="btn btn-warning return-btn" @click="showReturnModal"
                      :disabled="loadingReturn || submitted">
                      <span v-if="loadingReturn" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ (loadingReturn ? 'Returning...' : 'Return Memo') }}
                    </button>
                  </div> -->
                </template>

              </div>
            </div>


          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2';
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { bus } from '@/eventBus'
const userStore = useUserStore()

const router = useRouter();

dayjs.extend(utc)
dayjs.extend(timezone)

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const totalSteps = 1
const step = ref(1)
const loading = ref(false)
const loadingReturn = ref(false)
const loadingCreateMemo = ref(false)
const submitted = ref(false)
// const hasSavedRemark = ref(false)
const isEditable = ref(true)
const isRejected = ref(false)
const isReturned = ref(false)
const route = useRoute();
const id = route.params.id;
const payVoucherIdFromQuery = computed(() => {
  if (route.query.paymentVoucherId) return String(route.query.paymentVoucherId)
  return ''
})
const fromNotifications = computed(() => route.query.from === 'ViewPendingNotifications'  && route.query.entityType && route.query.statusInd)
// const actionRemarks = ref('')

const rolesLoaded = ref(false)
const isDataLoaded = ref(false)
const loadingMemo = ref(true)
const pvExistsForMemo = ref(false)
const memoExistsForPvMemo = ref(false)

const pv_memo = reactive({
  id: '',
  fileTitle: '',
  fileNumber: '',
  createdDate: '',
  mdaId: '',
  originatingMda: '',
  amountApproved: '',
  grpHeadGrpMemRemarks: '',
  memoEditable: '',
  groupTypeId: '',
  orgCode: '',
  ticketId: '',
  lastModifiedDate: '',
  createdBy: '',
  lastModBy: '',
  userId: '',
  isEditableInd: '',
  memoReturned: '',
  memoRejected: '',
  grpHeadGrpMemRemarksDate: '',
  grpHeadGrpMemId: '',
  version: ''
})

const form = reactive({
  id: '',
  fileTitle: '',
  fileNumber: '',
  createdDate: '',
  mdaId: '',
  originatingMda: '',
  amountApproved: '',
  grpHeadGrpMemRemarks: '',
  memoId: '',
})

const fetchMemo = async () => {
  const token = Cookies.get('auth_token');
  loadingMemo.value = true
  try {
    const { data } = await axios.get(`${root_api_url}/payment/memo/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    form.memoId = String(data?.id || id || '')

    pv_memo.id = data.id
    pv_memo.isEditableInd = data.isEditableInd
    pv_memo.memoEditable = Boolean(data.isEditableInd)
    pv_memo.groupTypeId = data.groupTypeId
    pv_memo.orgCode = data.orgCode
    pv_memo.ticketId = data.ticketId
    pv_memo.memoRejected = data.memoRejectedInd
    pv_memo.memoReturned = data.memoReturnedInd
    pv_memo.lastModifiedDate = data.lastModifiedDate
    pv_memo.createdBy = data.createdBy
    pv_memo.lastModBy = data.lastModBy || data.lastModifiedBy
    pv_memo.userId = data.userId
    pv_memo.grpHeadGrpMemRemarksDate = data.grpHeadGrpMemRemarksDate
    pv_memo.grpHeadGrpMemId = data.grpHeadGrpMemId
    pv_memo.version = data.version

    form.fileTitle = data.fileTitle
    form.fileNumber = data.fileNumber
    form.createdDate = data.createdDate
    form.mdaId = data.mdaId
    form.originatingMda = data.originatingMda
    form.amountApproved = data.amountApproved
    form.grpHeadGrpMemRemarks = data.grpHeadGrpMemRemarks
    isReturned.value = data.memoReturnedInd
    isEditable.value = data.isEditableInd,
      isRejected.value = data.memoRejectedInd,


      isDataLoaded.value = true


    await fetchAppraisalByFileNumber();
    await fetchMemoVoucherStatus(token)
    await fetchMemoByPvMemoId(token)
  } catch (error) {
    console.error('Error fetching pv_memo:', error);
    isDataLoaded.value = true
  } finally {
    loadingMemo.value = false
  }
};

const fetchMemoVoucherStatus = async (tokenFromCaller) => {
  const pvMemoId = pv_memo.id || id

  if (!pvMemoId) {
    pvExistsForMemo.value = false
    return
  }

  const token = tokenFromCaller || Cookies.get('auth_token')

  try {
    const { data } = await axios.get(`${root_api_url}/payment/memo-vouchers/${pvMemoId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })

    pvExistsForMemo.value = Array.isArray(data) ? data.length > 0 : Boolean(data?.id || data)
  } catch (error) {
    pvExistsForMemo.value = false
    console.error('Error checking memo voucher status:', error)
  }
}

const fetchMemoByPvMemoId = async (tokenFromCaller) => {
  const pvMemoId = pv_memo.id || id

  if (!pvMemoId) {
    memoExistsForPvMemo.value = false
    return
  }

  const token = tokenFromCaller || Cookies.get('auth_token')

  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/memo/pvMemo/${pvMemoId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })

    memoExistsForPvMemo.value = Array.isArray(data) ? data.length > 0 : Boolean(data?.id || data)
  } catch (error) {
    memoExistsForPvMemo.value = false
    console.error('Error checking memo by pvMemoId:', error)
  }
}

const handleCreateMemoFromPV = async () => {
  const token = Cookies.get('auth_token');

  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  const memoData = {
    fileTitle: form.fileTitle,
    fileNumber: form.fileNumber,
    mdaId: form.mdaId,
    originatingMda: form.originatingMda,
    amountApproved: form.amountApproved,
    groupTypeId: pv_memo.groupTypeId,
    ticketId: pv_memo.ticketId,
    grpHeadGrpMemId: pv_memo.grpHeadGrpMemId || userStore.profile?.id,
    pvMemoId: pv_memo.id || id,
    ...(payVoucherIdFromQuery.value ? { payVoucherId: payVoucherIdFromQuery.value } : {}),
  }

  loadingCreateMemo.value = true

  try {
    const { data } = await axios.post(`${root_api_url}/appraisal/memo`, memoData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const createdMemoId = data?.id || data?.memoId || data?.data?.id

    if (!createdMemoId) {
      throw new Error('Created memo ID was not returned by the server.')
    }

    Swal.fire({
      html: `${iconHtmlSuccess} <h1 style="font-weight:600;">Success</h1> <p>Memo Created Successfully</p> `,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    }).then(() => {
      bus.emit('refresh-notifications')
      router.push({ name: 'ViewMemo', params: { id: createdMemoId } })
    });

  } catch (error) {
    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || error?.response?.data?.message || 'Unexpected error occurred. Kindly contact administrator'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error.message || 'Unexpected error occurred. Kindly contact administrator'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    }
  } finally {
    loadingCreateMemo.value = false
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const allRoles = [
  'qssu',
  'dirOfBudget',
  'perSec',
  'commOfBAndP',
  'registryOfficer',
  'groupHead',
  'groupMember',
];

const hasAccess = (required) => {
  const profileRoles = userStore.profile?.role || {};

  const userHasRole = (role) => !!profileRoles[role];

  if (!required || required.length === 0) return true;

  if (required.includes('')) {
    const otherRoles = required.filter(role => role !== '');

    if (otherRoles.some(role => userHasRole(role))) return true;

    const isFreeUser = Object.keys(profileRoles).every(r => !allRoles.includes(r) || profileRoles[r] === false);
    if (isFreeUser) return true;

    return false;
  }

  return required.some(role => userHasRole(role));
}


const appraisalInitiated = ref(false)

const fetchAppraisalByFileNumber = async () => {
  try {
    const res = await axios.get(`${root_api_url}/appraisal/appraisal/pv_memo/${form.fileNumber}`)

    if (res.data) {
      appraisalInitiated.value = true
    }
  } catch (err) {
    console.error('Error fetching appraisal by file number:', err)
    appraisalInitiated.value = false
  }
}

const status = computed(() => {
  return pvExistsForMemo.value ? 'PV has been created for this memo' : 'Pending'
})

onMounted(async () => {
  await fetchMemo()
  document.title = "View PV Memo"

  if (userStore.profile?.role) {
    rolesLoaded.value = true
  } else {

    const stop = watch(
      () => userStore.profile?.role,
      (val) => {
        if (val) {
          rolesLoaded.value = true
          stop()
        }
      },
      { immediate: true }
    )
  }
})

</script>

<style scoped>
.memo-content-area {
  min-height: 280px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

/* Form Container */
.form-outer {
  width: 100%;
  max-width: 800px;
  /* padding: 1rem; */
}

.crud-form {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* .form-inner {
  padding: 1.5rem;
} */

.form-header h1 {
  font-size: 1.5rem;
  color: #333;
}

/* Form Row Layout */
.form-row {
  display: flex;
  margin-bottom: 1.5rem;
  gap: 1rem;
  align-items: flex-start;
}

.form-row-label {
  flex: 0 0 35%;
  text-align: right;
  padding-top: 0.5rem;
}

.form-row-label label {
  font-weight: 600;
    color: var(--color);
  margin-bottom: 0;
}

.form-row-input {
  flex: 1;
}

.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  box-shadow: none !important;
}

/* Action Buttons Container */
.action-buttons-container {
  padding: 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--bg);
}

.action-buttons-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-button {
  flex: 0 0 auto;
}

.action-button button {
  min-width: 140px;
  white-space: nowrap;
}

/* Button Styles */
.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.submit-btn {
  background-color: #007bff !important;
  border-color: #007bff !important;
}

.submit-btn:hover {
  background-color: #0056b3 !important;
}

.accept-btn {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

.accept-btn:hover {
  background-color: #218838 !important;
}

.generate-pv-btn {
  background-color: #17a2b8 !important;
  border-color: #17a2b8 !important;
}

.generate-pv-btn:hover {
  background-color: #138496 !important;
}

.return-btn {
  background-color: #ffc107 !important;
  border-color: #ffc107 !important;
  color: #212529 !important;
}

.return-btn:hover {
  background-color: #e0a800 !important;
}

/* Tablet Responsiveness (768px - 1024px) */
@media screen and (max-width: 1024px) {
  .form-outer {
    max-width: 100%;
    /* padding: 0.75rem; */
  }

  /* .form-inner {
    padding: 1.25rem;
  } */

  .form-row-label {
    flex: 0 0 40%;
  }
}

/* Mobile Responsiveness (below 768px) */
@media screen and (max-width: 768px) {
  /* .form-outer {
    padding: 0.5rem;
  }

  .form-inner {
    padding: 1rem;
  } */

  .form-header h1 {
    font-size: 1.25rem;
  }

  /* Stack label and input vertically on mobile */
  .form-row {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .form-row-label {
    flex: none;
    text-align: left;
    padding-top: 0;
  }

  .form-row-label label {
    font-size: 0.875rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-row-input {
    flex: none;
    width: 100%;
  }

  .view-only-input {
    font-size: 0.95rem;
    padding: 0.25rem 0;
    font-weight: 500;
  }

  /* Action buttons stack on mobile */
  .action-buttons-container {
    padding: 1rem;
  }

  .action-buttons-wrapper {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-button {
    width: 100%;
  }

  .action-button button {
    width: 100%;
    min-width: 100%;
  }

  /* Page identifier adjustments */
  .page-identifier {
    font-size: 0.875rem;
  }

  .page-identifier .material-symbols-outlined {
    font-size: 1.25rem;
  }
}

/* Small Mobile (below 480px) */
@media screen and (max-width: 480px) {
  /* .form-outer {
    padding: 0.25rem;
  }

  .form-inner {
    padding: 0.75rem;
  } */

  .form-header h1 {
    font-size: 1.125rem;
  }

  .form-row {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }

  .form-row-label label {
    font-size: 0.8125rem;
  }

  .view-only-input {
    font-size: 0.875rem;
  }

  .action-buttons-container {
    padding: 0.75rem;
  }

  .btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
}

/* Loading State */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

/* Print Styles */
@media print {

  .action-buttons-container,
  .page-identifier {
    display: none !important;
  }

  .form-outer {
    max-width: 100%;
    box-shadow: none;
  }

  .form-row {
    page-break-inside: avoid;
  }
}
</style>
